import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { geldText, erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { fetchRequests, leistungenPreisspiegel, leistungenUebernehmen, type LeistungsZeile } from '../../api/calculation-requests.api'
import type { RequestRow } from '../../types/calculation-requests.types'
import type { Calculation } from '../../types/calculation.types'
import { STATUS_ANFRAGE } from './AnfrageSeitenfeld'
import { NuAnfrageDialog } from './NuAnfrageDialog'

/**
 * AP-37 Detailblatt K 7.2 — Bereich **Nachunternehmer** im Reiter LV: Knopf „An
 * Nachunternehmer", die Anfragen der Art Leistung dieser Kalkulation mit Status, Frist
 * und Antworten (Sprung ins Cockpit Materialanfragen), der **Preisspiegel mit Positionen**
 * und „Übernehmen" als Kostenzeile Fremdleistung (K-14) mit Vorschau der Angebotssumme.
 */
export function NuBereich({ kalkulation, lvFormat, onGeaendert }: { kalkulation: Calculation; lvFormat?: string | null; onGeaendert: () => void }) {
  const { user } = useAuthStore()
  const darfAnfragen = erfuelltRecht('calculation.request.manage', user?.permissions) && erfuelltRecht('calculation.lv.export', user?.permissions)
  const kosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  const darfUebernehmen = kosten && kalkulation.writable && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const [anfragen, setAnfragen] = useState<RequestRow[]>([])
  const [spiegel, setSpiegel] = useState<LeistungsZeile[]>([])
  const [wahl, setWahl] = useState<Record<string, string>>({})
  const [dialog, setDialog] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      setAnfragen((await fetchRequests({ calculationId: kalkulation.id, kind: 'SERVICES' })).data)
      if (kosten) {
        const s = await leistungenPreisspiegel(kalkulation.id)
        setSpiegel(s)
        setWahl(Object.fromEntries(s.map((z) => [z.positionId, (z.offers.find((o) => o.applied) ?? z.offers.find((o) => o.best))?.requestLineId ?? ''])))
      }
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Anfragen ließen sich nicht laden.'))
    }
  }, [kalkulation.id, kosten])
  useEffect(() => {
    void laden()
  }, [laden, kalkulation.updatedAt])

  const uebernehmen = async () => {
    const choices = Object.entries(wahl)
      .filter(([, l]) => l)
      .map(([positionId, requestLineId]) => ({ positionId, requestLineId }))
    if (!choices.length) return
    setFehler(null)
    try {
      const v = await leistungenUebernehmen({ calculationId: kalkulation.id, choices, dryRun: true })
      if (!(await bestaetige(`${choices.length} Position(en) als Fremdleistung übernehmen — Angebotssumme ${geldText(v.priceBefore)} → ${geldText(v.priceAfter)}?`, { ok: 'Übernehmen' }))) return
      await leistungenUebernehmen({ calculationId: kalkulation.id, choices })
      onGeaendert()
      void laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Übernahme ist fehlgeschlagen.'))
    }
  }

  const lieferanten = [...new Map(spiegel.flatMap((z) => z.offers.map((o) => [o.supplierId, o.supplierName] as const))).entries()]

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Nachunternehmer</h3>
        <div className="flex gap-2">
          {anfragen.length > 0 && (
            <Link to={`/calculations/material-requests?tab=anfragen`} className="text-xs text-primary hover:underline">
              im Cockpit Materialanfragen
            </Link>
          )}
          {darfAnfragen && (
            <button type="button" className="rounded-md border border-border px-2.5 py-1 text-sm" onClick={() => setDialog(true)}>
              An Nachunternehmer …
            </button>
          )}
        </div>
      </div>
      {anfragen.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Noch keine Anfrage. Positionen an Nachunternehmer anfragen — mit Teil-LV als {lvFormat === 'GAEB' ? 'GAEB X83' : 'ONLV'} oder Excel.
        </p>
      ) : (
        <table className="mb-3 w-full text-sm">
          <tbody>
            {anfragen.map((a) => (
              <tr key={a.id} className="border-t border-border/60">
                <td className="py-1 pr-2">{a.supplier.name}</td>
                <td className="py-1 pr-2 text-xs text-muted-foreground">{a.documentNumber ?? ''}</td>
                <td className="py-1 pr-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${a.overdue ? 'bg-destructive/15 text-destructive' : STATUS_ANFRAGE[a.status].klasse}`}>{STATUS_ANFRAGE[a.status].label}</span>
                </td>
                <td className="py-1 pr-2 text-xs text-muted-foreground">{a.dueAt ? `bis ${new Date(a.dueAt).toLocaleDateString('de-DE')}` : ''}</td>
                <td className="py-1 text-right text-xs tabular-nums">
                  {a.linesAnswered}/{a.linesTotal} beantwortet
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {kosten && spiegel.length > 0 && (
        <>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preisspiegel je Position (EP netto)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="py-1 pr-2 text-left font-normal">Position</th>
                  {lieferanten.map(([id, name]) => (
                    <th key={id} className="py-1 pr-2 text-right font-normal">
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {spiegel.map((z) => (
                  <tr key={z.positionId} className="border-t border-border/60">
                    <td className="py-1 pr-2">
                      <span className="tabular-nums text-muted-foreground">{z.label}</span> {z.shortText}
                    </td>
                    {lieferanten.map(([id]) => {
                      const o = z.offers.find((x) => x.supplierId === id)
                      if (!o) return <td key={id} />
                      return (
                        <td key={id} className={`py-1 pr-2 text-right tabular-nums ${o.best ? 'font-semibold text-emerald-700 dark:text-emerald-300' : ''}`} title={o.note ?? undefined}>
                          <label className="inline-flex items-center gap-1">
                            {o.notOffered ? 'n. a.' : o.net === null ? '—' : geldText(o.net)}
                            {o.applied && <span className="text-[10px] text-primary">übernommen</span>}
                            {darfUebernehmen && o.net !== null && (
                              <input type="radio" name={`nu-${z.positionId}`} checked={wahl[z.positionId] === o.requestLineId} onChange={() => setWahl((w) => ({ ...w, [z.positionId]: o.requestLineId }))} />
                            )}
                          </label>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {darfUebernehmen && (
            <div className="mt-2 flex justify-end">
              <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground" onClick={() => void uebernehmen()}>
                Übernehmen …
              </button>
            </div>
          )}
        </>
      )}
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      {dialog && (
        <NuAnfrageDialog
          kalkulation={kalkulation}
          lvFormat={lvFormat}
          vorgewaehlt={[]}
          onClose={() => setDialog(false)}
          onDone={() => {
            setDialog(false)
            void laden()
          }}
        />
      )}
    </section>
  )
}
